import React, { useEffect, useRef, useState } from "react";
import { useRouterStore } from "../../Stores/Router";
import { useFeatureRequestStore } from "../../Stores/FeatureRequests";
import { useConfigStore } from "../../Stores/Config";
import { generateHTML } from "@tiptap/react";
import ExtensionKit from "../../Components/tiptap/extenstions/extension-kit";
import moment from "moment";
import "./FeatureRequestDetails.scss";
import {
  getStatusIcon,
  ticketStatusToColor,
} from "../../Helper/FeatureRequestStatusHelper";
import { useSessionStore } from "../../Stores/Session";
import EmailUpvoteDialog from "../../Components/EmailUpvoteDialog/EmailUpvoteDialog";
import { activateOnKey } from "../../Helper/a11y";
import { sanitizeUserContent } from "../../Helper/sanitize";
import { shouldShowCommentsSection } from "../../Helper/FeatureRequestComments";

const avatarColors = [
  "#6366f1",
  "#8b5cf6",
  "#a855f7",
  "#ec4899",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#14b8a6",
  "#06b6d4",
  "#3b82f6",
  "#6d28d9",
];

const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

const getInitial = (name: string) => {
  return (name || "Guest").charAt(0).toUpperCase();
};

const FeatureRequestDetails = () => {
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionClamped, setDescriptionClamped] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const routerStore = useRouterStore();
  const session = useSessionStore((state) => state.session);
  const featureRequestStore = useFeatureRequestStore();
  const configStore = useConfigStore();
  const [commentText, setCommentText] = useState("");
  // Id of the request whose load failed (deleted, or a project without the
  // roadmap: the API answers 404 or 402). Keyed by id so a late failure of a
  // previously opened request never marks the current one.
  const [failedRequestId, setFailedRequestId] = useState<string | null>(null);
  const featureRequest = featureRequestStore.currentFeatureRequest;
  const comments = featureRequestStore.currentComments;
  const bgColor = ticketStatusToColor(featureRequest?.status, 15);
  const textColor = ticketStatusToColor(featureRequest?.status, 100);
  const desc = featureRequest?.formData?.description;
  const isRichText = typeof desc === "object" && desc !== null && desc?.type;
  // Visitors write feature requests and comments: sanitise with the visitor
  // policy (no embeds, form controls, styles or data-action triggers).
  const html = sanitizeUserContent(
    isRichText ? generateHTML(desc, ExtensionKit()) : (desc ?? ""),
  );

  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) {
      setDescriptionClamped(false);
      return;
    }

    setDescriptionClamped(el.scrollHeight > el.clientHeight + 2);
  }, [html]);

  const statusLabel = featureRequestStore.statusOptions.find(
    (option) => option.value === featureRequest?.status,
  )?.label;
  const config = useConfigStore((state) => state.config);

  let writeCommentPermission = true;
  let upvotePermission = true;

  if (config?.enabVoting === "nobody") {
    upvotePermission = false;
  } else if (config?.enabVoting === "identified") {
    upvotePermission = session?.userId !== undefined && session?.userId !== "";
  }

  if (config?.enableUserComm === "nobody") {
    writeCommentPermission = false;
  } else if (config?.enableUserComm === "identified") {
    writeCommentPermission =
      session?.userId !== undefined && session?.userId !== "";
  }

  const showCommentsSection = shouldShowCommentsSection({
    enablePublicComments: config?.enablePublicComments,
    isIdentifiedUser: session?.userId !== undefined && session?.userId !== "",
    commentCount: comments.length,
  });

  useEffect(() => {
    // currentData is the router's global page payload. When another page is
    // pushed on top (e.g. a help center article), this still-mounted view
    // would otherwise re-fire with that foreign id and load it as a feature
    // request, clobbering the current one (same pattern as #145828).
    if (routerStore.currentPage !== "feature-request-details") {
      return;
    }
    if (routerStore.currentData && routerStore.currentData.id) {
      const requestId = routerStore.currentData.id;
      setFailedRequestId((failed) => (failed === requestId ? null : failed));
      featureRequestStore.loadFeatureRequest(requestId).catch(() => {
        setFailedRequestId(requestId);
      });
    }

    return () => {
      featureRequestStore.setCurrentFeatureRequest(undefined);
    };
  }, [routerStore.currentData]);

  const upvotes = featureRequest?.upvotesCount ?? 0;
  const createdAt = featureRequest?.createdAt
    ? moment(featureRequest.createdAt).format("DD. MMM YYYY HH:mm")
    : "";

  const handleUpvote = (email: string) => {
    if (!upvotePermission) {
      return;
    }
    if (!email) {
      setShowEmailDialog(true);
      return;
    }
    if (featureRequest?.shareToken) {
      featureRequestStore.upvoteFeatureRequest(
        featureRequest.shareToken,
        email,
      );
    }
  };

  const handleSendComment = (email: string) => {
    if (!writeCommentPermission) {
      return;
    }
    if (commentText.trim() && featureRequest?.shareToken) {
      if (!email) {
        setShowEmailDialog(true);
        return;
      }
      featureRequestStore.addComment(
        featureRequest.shareToken,
        commentText.trim(),
      );
      setCommentText("");
    }
  };

  // Not loaded: "Nothing found" instead of a skeleton that stays forever.
  if (
    !featureRequest &&
    failedRequestId !== null &&
    failedRequestId === routerStore.currentData?.id
  ) {
    return (
      <div className="feature-request-details">
        <div className="feature-request-details-empty" role="status">
          {configStore.translateTextWithFallback(
            "nothingFoundTitle",
            "Nothing found",
          )}
        </div>
      </div>
    );
  }

  // Loading: the store clears the previous request on open, so render a calm
  // skeleton instead of an empty header + "0" votes + stale comments.
  if (!featureRequest) {
    return (
      <div className="feature-request-details" aria-busy="true">
        <div className="feature-request-details-skeleton">
          <div className="feature-request-details-skeleton-row">
            <div className="feature-request-details-skeleton-box" />
            <div className="feature-request-details-skeleton-lines">
              <div className="feature-request-details-skeleton-line feature-request-details-skeleton-line--wide" />
              <div className="feature-request-details-skeleton-line feature-request-details-skeleton-line--pill" />
            </div>
          </div>
          <div className="feature-request-details-skeleton-line" />
          <div className="feature-request-details-skeleton-line feature-request-details-skeleton-line--short" />
        </div>
      </div>
    );
  }

  return (
    <div className="feature-request-details">
      <div className="feature-request-details-header">
        <div
          className={`feature-request-details-upvotes ${featureRequest?.upvoted ? "feature-request-details-upvotes--upvoted" : ""}`}
          role="button"
          tabIndex={0}
          aria-label="Upvote"
          aria-pressed={!!featureRequest?.upvoted}
          onClick={() => handleUpvote(session?.email ?? "")}
          onKeyDown={activateOnKey(() => handleUpvote(session?.email ?? ""))}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className=""
          >
            <path d="m18 15-6-6-6 6" />
          </svg>
          <span>{upvotes > 99 ? "99+" : upvotes}</span>
        </div>
        <div className="feature-request-details-info">
          <div className="feature-request-details-title" role="heading" aria-level={1}>
            {featureRequest?.title}
          </div>
          <div className="feature-request-details-meta">
            {statusLabel && (
              <span
                className="feature-request-details-status"
                style={{
                  color: textColor,
                  backgroundColor: bgColor,
                }}
              >
                {getStatusIcon[featureRequest?.status ?? "OPEN"] ? (
                  React.createElement(
                    getStatusIcon[featureRequest?.status ?? "OPEN"],
                  )
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 6a2 2 0 0 1 3.414-1.414l6 6a2 2 0 0 1 0 2.828l-6 6A2 2 0 0 1 12 18z" />
                    <path d="M2 6a2 2 0 0 1 3.414-1.414l6 6a2 2 0 0 1 0 2.828l-6 6A2 2 0 0 1 2 18z" />
                  </svg>
                )}
                {statusLabel}
              </span>
            )}
            <span className="feature-request-details-date">{createdAt}</span>
          </div>
        </div>
      </div>

      <div className="feature-request-details-body">
        {html && (
          <>
            <div className="feature-request-details-section-title" role="heading" aria-level={2}>
              {configStore.translateText("description")}
            </div>
            <div
              ref={descriptionRef}
              className={`feature-request-details-description ${!descriptionExpanded ? "feature-request-details-description--clamped" : ""}`}
              dangerouslySetInnerHTML={{ __html: html }}
            />
            {descriptionClamped && (
              <div
                className="feature-request-details-read-more"
                role="button"
                tabIndex={0}
                aria-expanded={descriptionExpanded}
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                onKeyDown={activateOnKey(() => setDescriptionExpanded(!descriptionExpanded))}
              >
                {descriptionExpanded
                  ? configStore.translateText("showLess")
                  : configStore.translateText("showMore")}
              </div>
            )}
          </>
        )}
      </div>

      {showCommentsSection && (
        <>
        <div className="feature-request-details-divider" />

        <div className="feature-request-details-comments">
          {comments.length === 0 ? (
            <div className="feature-request-details-no-comments">
              {configStore.translateText("noComments")}
            </div>
          ) : (
            <div className="feature-request-details-comments-list">
              {comments.map((comment, index) => {
                let rawContent = comment.data?.content ?? comment.data;
                if (rawContent?.text) {
                  rawContent = rawContent.text;
                }

                const isRichText =
                  typeof rawContent === "object" &&
                  rawContent !== null &&
                  "type" in rawContent;
                const content = sanitizeUserContent(
                  isRichText
                    ? generateHTML(
                        rawContent as Record<string, unknown>,
                        ExtensionKit(),
                      )
                    : String(rawContent ?? ""),
                );

                const isTeamMember = !!comment.user;
                const displayName =
                  comment.session?.name ||
                  [comment.user?.firstName, comment.user?.lastName]
                    .filter(Boolean)
                    .join(" ") ||
                  "Guest";
                const profileImage = comment.user?.profileImageUrl;
                const initial = getInitial(displayName);
                const avatarColor = getAvatarColor(displayName);

                return (
                  <div
                    key={comment.id || index}
                    className="feature-request-details-comment"
                  >
                    <div className="feature-request-details-comment-avatar-wrap">
                      {profileImage ? (
                        <div className="feature-request-details-comment-avatar-img-wrap">
                          <img
                            className="feature-request-details-comment-avatar-img"
                            src={profileImage}
                            alt={displayName}
                          />
                          {isTeamMember && (
                            <svg
                              className="feature-request-details-comment-badge"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
                              <path d="m9 12 2 2 4-4" />
                            </svg>
                          )}
                        </div>
                      ) : (
                        <div
                          className="feature-request-details-comment-avatar-initial"
                          style={{ backgroundColor: avatarColor }}
                        >
                          {initial}
                        </div>
                      )}
                    </div>
                    <div className="feature-request-details-comment-bubble">
                      <div
                        className="feature-request-details-comment-text"
                        dangerouslySetInnerHTML={{ __html: content }}
                      />
                      <div className="feature-request-details-comment-date">
                        {comment.createdAt
                          ? moment(comment.createdAt).format("DD. MMM YYYY")
                          : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </>
      )}

      {writeCommentPermission && (
        <div className="feature-request-details-composer">
          <textarea
            className="feature-request-details-composer-input"
            aria-label={configStore.translateText("writeComment")}
            placeholder={configStore.translateText("writeComment")}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          />
          <button
            className="feature-request-details-composer-send"
            onClick={() => handleSendComment(session?.email ?? "")}
            disabled={!commentText.trim()}
          >
            {configStore.translateText("send")}
          </button>
        </div>
      )}
      <EmailUpvoteDialog
        open={showEmailDialog}
        onClose={() => setShowEmailDialog(false)}
        onSubmit={(email) => handleUpvote(email)}
      />
    </div>
  );
};

export default FeatureRequestDetails;
